/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Key-value details attached to a logging event.
 */
export type LogDetails = Record<string, string>;

export const DiagnosticSeverity = {
  Debug: 0,
  Info: 1,
  Warning: 2,
  Error: 3
} as const;

export type DiagnosticSeverity =
  (typeof DiagnosticSeverity)[keyof typeof DiagnosticSeverity];

/**
 * A diagnostic log entry queued in IndexedDB while Sage identity
 * (bodyId / partnerId) is not yet available.
 *
 * @deprecated Superseded by {@link TPendingTelemetryLog}. Retained so the
 * legacy `pendingDiagnosticLogs` storage key written by earlier builds can
 * still be drained on upgrade.
 */
export interface TPendingDiagnosticLog {
  sequence: number;
  timestamp: number;
  name: string;
  message: string;
  severity: number;
  details?: LogDetails;
}

/**
 * Which Sage stream a queued entry is replayed onto once identity lands.
 */
export const TelemetryLogType = {
  Diagnostic: "diagnostic",
  Operational: "operational",
  Analytics: "analytics"
} as const;

export type TelemetryLogType =
  (typeof TelemetryLogType)[keyof typeof TelemetryLogType];

/**
 * A telemetry entry queued in IndexedDB while Sage identity (bodyId /
 * partnerId) is not yet available.
 *
 * Sage stamps `apiKey`, `sessionId` and `bodyId` onto an event at log time,
 * not at send time, so an event emitted before provisioning would otherwise
 * ship with the default body id — and an attempt that fails never reaches
 * provisioning at all. Queueing defers the stamp until identity exists.
 *
 * `message` / `severity` apply only to diagnostics and `eventType` only to
 * analytics; both are absent on the streams that have no such field.
 */
export interface TPendingTelemetryLog {
  sequence: number;
  timestamp: number;
  logType: TelemetryLogType;
  name: string;
  message?: string;
  severity?: number;
  eventType?: string;
  details?: LogDetails;
}

export const OperationalTransaction = {
  WatchContent: "watchContentTransaction",
  StreamingSession: "streamingSessionTransaction"
} as const;

export type OperationalTransactionName =
  (typeof OperationalTransaction)[keyof typeof OperationalTransaction];
